// Aggregates a flat list of Media records and a list of VariantGroups into
// the unified tile stream that the gallery renders.
//
// `preferOrientation` controls which half of a Hoch+Quer pair becomes the
// visible primary tile:
//   0 = Querformat (default — gallery tiles read landscape-first)
//   1 = Hochkant (used when the operator filters explicitly for portrait
//                 so the visible thumbnail matches their filter intent)
//
// Three tile shapes survive the reducer:
//
//   1. Standalone media — passes through unchanged plus the meta flags
//      `_pair_partner: null`, `_is_pair: false`, `_is_variant_group: false`.
//
//   2. Hoch+Quer pair — two media that share a `pair_id`. They collapse into
//      one tile carrying the preferred-orientation half as the primary plus
//      `_pair_partner` for the other half.
//
//   3. Variant group — a synthetic tile that mirrors the Media shape so the
//      regular ListItem renderer keeps working. Powered by the group's
//      default variant (or first variant): name, thumbnail and dimensions
//      come from there. Carries `_is_variant_group: true`, `_variant_count`
//      and `_variant_group` (the full group object) so click handlers can
//      route to the group form.
//
// Media that already lives inside a variant group is *removed* from the
// flat list so it doesn't appear twice. The group tile is the canonical
// representation; the underlying media surfaces only inside the group form.
export function aggregateByPair(medias, variantGroups = [], { preferOrientation = 0 } = {}) {
  if (!Array.isArray(medias)) medias = [];
  if (!Array.isArray(variantGroups)) variantGroups = [];

  // Build the membership set first so we can prune media before aggregation.
  const groupedMediaIds = collectGroupedMediaIds(variantGroups);

  const tiles = [];
  const seenPairs = new Map();

  for (const m of medias) {
    if (groupedMediaIds.has(m.model.ID)) continue;

    const pid = m.pair_id || '';
    if (!pid) {
      tiles.push(decorateMedia(m, { partner: null, isPair: false }));
      continue;
    }
    if (seenPairs.has(pid)) {
      const idx = seenPairs.get(pid);
      const tile = tiles[idx];
      // Already complete (backend attached the partner) — skip duplicate.
      if (tile._pair_partner) continue;
      // Promote the new half to primary if it matches the preferred
      // orientation while the existing primary doesn't.
      if (m.orientation === preferOrientation && tile.orientation !== preferOrientation) {
        tiles[idx] = decorateMedia(m, { partner: stripMeta(tile), isPair: true });
      } else {
        tiles[idx] = { ...tile, _pair_partner: stripMeta(m) };
      }
      continue;
    }
    seenPairs.set(pid, tiles.length);
    // Use the backend's pre-attached partner so the tile is complete even
    // when the partner half isn't on the same pagination page. If the
    // partner matches the preferred orientation and `m` doesn't, swap roles
    // so the visible primary is the half the operator expects.
    const backendPartner = m.pair_partner || null;
    if (
      backendPartner &&
      backendPartner.orientation === preferOrientation &&
      m.orientation !== preferOrientation
    ) {
      tiles.push(decorateMedia(backendPartner, {
        partner: stripMeta(m),
        isPair: true,
      }));
    } else {
      tiles.push(decorateMedia(m, {
        partner: backendPartner ? stripMeta(backendPartner) : null,
        isPair: true,
      }));
    }
  }

  // Variant groups are fetched from a separate endpoint so the API can't
  // pre-merge them into the sorted media stream. We append them and then
  // run a *stable* sort on the combined list using the same key the API
  // uses for media: (highlighted DESC, model.CreatedAt DESC). For media
  // tiles the keys come straight from the backend, so their relative
  // order is preserved (stable sort keeps equal-key items in place). For
  // group tiles the CreatedAt is derived from the newest variant link —
  // a brand-new variant slot bumps the group up, a typo fix does not.
  for (const g of variantGroups) {
    const tile = variantGroupTile(g, preferOrientation);
    if (tile) tiles.push(tile);
  }

  tiles.sort((a, b) => {
    const ha = a.highlighted ? 1 : 0;
    const hb = b.highlighted ? 1 : 0;
    if (ha !== hb) return hb - ha;
    const ta = a.model?.CreatedAt ? new Date(a.model.CreatedAt).getTime() : 0;
    const tb = b.model?.CreatedAt ? new Date(b.model.CreatedAt).getTime() : 0;
    return tb - ta;
  });

  return tiles;
}

function collectGroupedMediaIds(variantGroups) {
  const ids = new Set();
  for (const g of variantGroups) {
    for (const v of g.variants || []) {
      if (v.landscape_media_id) ids.add(v.landscape_media_id);
      if (v.portrait_media_id) ids.add(v.portrait_media_id);
    }
  }
  return ids;
}

// pickGroupPreviewMedia chooses the media that visually represents a
// group in the gallery / dashboard panel. `preferOrientation` flips
// which slot is tried first:
//   0 (default, landscape) → try the default variant's landscape, then
//     scan every variant for a landscape slot, only then fall back to
//     a portrait (default variant first, then any variant).
//   1 (portrait) → mirror of the above with portrait preferred.
// The cross-variant scan matters for the dashboard panels: an operator
// might mark the variant *without* a landscape slot as default — we
// still want the panel tile to show a landscape image whenever any
// variant in the group has one, instead of falling straight to the
// default variant's portrait.
function pickGroupPreviewMedia(group, preferOrientation = 0) {
  const variants = group.variants || [];
  if (variants.length === 0) return null;
  const defaultVariant =
    variants.find((v) => v.model.ID === group.default_variant_id) ||
    variants[0];

  const firstAcross = (key) => {
    for (const v of variants) {
      if (v?.[key]) return v[key];
    }
    return null;
  };

  if (preferOrientation === 1) {
    return (
      defaultVariant?.portrait_media ||
      firstAcross('portrait_media') ||
      defaultVariant?.landscape_media ||
      firstAcross('landscape_media') ||
      null
    );
  }
  return (
    defaultVariant?.landscape_media ||
    firstAcross('landscape_media') ||
    defaultVariant?.portrait_media ||
    firstAcross('portrait_media') ||
    null
  );
}

// variantGroupTile builds a Media-shaped synthetic tile that the regular
// list renderer can display without special-casing every prop. Exported
// so the dashboard panels — which now receive pre-aggregated tiles from
// the server — can still go through the same tile-construction path
// when they need to render a variant_group tile.
export function variantGroupTile(group, preferOrientation = 0, aggregate = {}) {
  const preview = pickGroupPreviewMedia(group, preferOrientation);
  if (!preview) return null;

  // OR-merge the "highlighted" / "new" aggregates across every member media,
  // not just the preview. SyncSettings writes the same highlight window to
  // every member, but if any one of them ever falls out of sync (manual
  // edit on a single media inside the group) the group tile should still
  // surface the highlight as long as *something* in it is active.
  let highlighted = aggregate.highlighted ? 1 : (preview.highlighted || 0);
  let isNew = aggregate.new ? 1 : (preview.new || 0);
  for (const v of group.variants || []) {
    for (const m of [v.landscape_media, v.portrait_media]) {
      if (!m) continue;
      if (m.highlighted) highlighted = 1;
      if (m.new) isNew = 1;
    }
  }

  // Effective CreatedAt: max(member-media CreatedAt). Mirrors the
  // server's variantGroupEffectiveCreatedAt — see aggregated_tiles.go
  // for the rationale. Briefly: variant-link rows track admin activity
  // ("when the operator wired this up"), not content recency, so
  // wrapping old elements into a fresh group should NOT push it to the
  // top of "neueste zuerst". Reading media.CreatedAt keeps the group
  // anchored to the actual content's age.
  let createdAt = null;
  for (const v of group.variants || []) {
    for (const m of [v.landscape_media, v.portrait_media]) {
      const ts = m?.model?.CreatedAt;
      if (ts && (!createdAt || ts > createdAt)) createdAt = ts;
    }
  }
  if (aggregate.created_at) createdAt = aggregate.created_at;
  if (!createdAt) createdAt = group.model?.CreatedAt || null;

  return {
    // Mirror the Media shape just enough for ListItem and click handlers.
    model: {
      ID: preview.model.ID,
      UpdatedAt: group.model.UpdatedAt,
      CreatedAt: createdAt,
    },
    name: group.name,
    description: group.description || '',
    type: preview.type,
    orientation: preview.orientation,
    real_width: preview.real_width,
    real_height: preview.real_height,
    s3_url: preview.s3_url,
    s3_med_url: preview.s3_med_url,
    s3_thumb_url: preview.s3_thumb_url,
    s3_pixel_thumbnail_url: preview.s3_pixel_thumbnail_url,
    public: preview.public,
    organisation_id: preview.organisation_id,
    // Flags ListItem reads to render the "highlighted" border / "Neu" badge.
    highlighted,
    new: isNew,
    highlight: preview.highlight,
    highlight_start: preview.highlight_start,
    highlight_end: preview.highlight_end,
    // Expose group + tag assignments from the preview's media so the bulk
    // "Gruppen zuordnen" modal can show the existing assignments as
    // greyed-out chips when a variant-group tile is part of the selection.
    // SyncSettings keeps every member-media in lockstep on these fields,
    // so reading them off the preview is representative for the entire
    // group.
    groups: preview.groups || [],
    tags: preview.tags || [],

    // Frontend-only meta — used by the renderer / click handlers.
    _is_variant_group: true,
    _variant_group: group,
    _variant_count: (group.variants || []).length,
    _pair_partner: null,
    _is_pair: false,
  };
}

function decorateMedia(m, { partner, isPair }) {
  // Both halves of a pair represent the same content, so the tile should
  // surface as highlighted (and as new) whenever *either* half carries the
  // flag. The backend mirror keeps these in sync going forward, but the
  // OR-merge means tiles already render correctly even on data that was
  // created before the mirror existed.
  const partnerHighlighted = partner?.highlighted ? 1 : 0;
  const partnerNew = partner?.new ? 1 : 0;
  return {
    ...m,
    highlighted: m.highlighted || partnerHighlighted ? 1 : 0,
    new: m.new || partnerNew ? 1 : 0,
    _pair_partner: partner,
    _is_pair: isPair,
    _is_variant_group: false,
  };
}

function stripMeta(m) {
  // eslint-disable-next-line no-unused-vars
  const { _pair_partner, _is_pair, _is_variant_group, _variant_group, _variant_count, pair_partner, ...rest } = m;
  return rest;
}
